import React, { useEffect } from 'react';
import { TextInputHookForm } from 'bench-internal-theme';
import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import Button from 'primitive-components/Button';
import moment, { Moment } from 'moment';

const InputLayout = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr 1fr;

  }
`;

const DateInput = styled(TextInputHookForm)`
  max-height: 45px;
`;

const SubmitButton = styled(Button).attrs(() => ({
  size: 'small',
  styleType: 'primary',
}))`
  height: 45px;
`;

type DatePickerProps = {
  handleSubmission: ({ startDate, endDate }: Record<string, string>) => unknown;
  defaultStartDate: Moment | undefined | null;
  defaultEndDate: Moment | undefined | null;
  loading: boolean;
};

export const DatePicker = ({
  loading,
  handleSubmission,
  defaultStartDate,
  defaultEndDate,
}: DatePickerProps) => {
  const methods = useForm();
  const { handleSubmit, watch, setValue } = methods;
  const startDate = watch('startDate', undefined);
  const endDate = watch('endDate', undefined);

  useEffect(() => {
    if (defaultStartDate) {
      setValue('startDate', defaultStartDate.format('YYYY-MM-DD'));
    }
    if (defaultEndDate) {
      setValue('endDate', defaultEndDate.format('YYYY-MM-DD'));
    }
  }, [defaultStartDate, defaultEndDate, setValue, loading]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(formValues => handleSubmission(formValues))}>
        <InputLayout>
          <DateInput
            aria-label="Start Date"
            name="startDate"
            rules={{
              required: {
                value: true,
                message: 'Start date is required',
              },
              validate: {
                isBeforeEndDate: (value: string | undefined) =>
                  (endDate && value && moment(value).isBefore(moment(endDate))) ||
                  moment(value).isSame(endDate) ||
                  !endDate ||
                  'Cannot be after end date',
              },
            }}
            type="date"
          />
          <DateInput
            aria-label="End Date"
            name="endDate"
            rules={{
              required: { value: true, message: 'End date is required' },
              validate: {
                isAfterStartDate: (value: string | undefined) =>
                  (startDate &&
                    value &&
                    (moment(value).isAfter(moment(startDate)) ||
                      moment(value).isSame(moment(startDate)))) ||
                  'Cannot be before start date',
                isBeforeToday: (value: string | undefined) =>
                  (value && (moment(value).isBefore(moment()) || moment(value).isSame(moment()))) ||
                  'Cannot be before today',
              },
            }}
            type="date"
          />
          <SubmitButton disabled={loading}>Submit</SubmitButton>
        </InputLayout>
      </form>
    </FormProvider>
  );
};
